/**
 * `product_type` on the `booking_complete` event (AHD-11220).
 *
 * Marketing reads this to compute CAC, so it has to separate bookings that WIN
 * a customer from bookings a customer we already have comes back to make. A
 * follow-up used to be invisible here: it is a blood-only package like any
 * other, so it emitted `blood_only` and inflated acquisition cost with revenue
 * that acquired nobody.
 *
 * A booking is a follow-up when the package it books is one — the catalogue
 * says so through `stripe_metadata_id`, the mode-agnostic handle (`prod_…` ids
 * differ between Stripe's live and sandbox accounts, so they are the wrong
 * thing to key on). `followup-blood-test` is the only such product.
 *
 * There is deliberately no MRI follow-up label: no MRI follow-up product
 * exists, and the app has no other notion of a repeat scan. A `followup-…` MRI
 * package would report `mri_package` like any other scan — when such a product
 * ships, decide then what it should emit rather than guessing now.
 *
 * `membership` still wins over everything: a redemption is already excludable
 * by that label, and splitting it further would change a bucket marketing
 * already reports on.
 */
import type { Product } from "@/components/product-step/types";

export type BookingProductType =
  | "membership"
  | "blood_follow-up"
  | "blood_only"
  | "mri_package";

/** Blood-only packages carry `package_types: ['blood-only']`; MRI ones `['mri']`. */
const isBloodOnly = (product: Product): boolean =>
  product.package_types?.includes("blood-only") ?? false;

const isPackage = (product: Product): boolean =>
  product.product_type === "package";

/** The catalogue's own naming: `followup-blood-test`. */
const isFollowup = (product: Product): boolean =>
  (product.stripe_metadata_id ?? "").startsWith("followup-");

export const bookingProductType = (
  products: Product[],
  isMember: boolean,
): BookingProductType => {
  if (isMember) return "membership";

  const packages = products.filter(isPackage);
  if (packages.some((p) => isFollowup(p) && isBloodOnly(p))) {
    return "blood_follow-up";
  }

  return packages.some(isBloodOnly) ? "blood_only" : "mri_package";
};
